import React, { useState, useEffect } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import {
  LineChart, Line, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer
} from 'recharts';
import { Sun, Zap, Activity, Thermometer, Battery } from 'lucide-react';
import { format } from 'date-fns';
import '../components/AdvancedDashboard.css';
import './pages.css';
import { useAuth } from '../auth';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import { LoadingState, EmptyState, ErrorState } from '../components/ui/States';
import { fetchPanelFieldHistory, mergeFieldSeries } from '../historyApi';

const PANEL_HISTORY_FIELDS = ['power_w', 'voltage_v', 'current_a', 'temp_panel_c'];

export default function PanelDetailPage() {
  const { plantId, panelId } = useParams();
  const { realTimeData, plants } = useOutletContext();
  const { token } = useAuth();
  const [timeRange, setTimeRange] = useState('1h');
  const [historyData, setHistoryData] = useState([]);
  const [historyStatus, setHistoryStatus] = useState('loading');

  const plantMeta = Array.isArray(plants) ? plants.find(p => p.plant_id === plantId) : null;
  const realTimeArray = Array.from(realTimeData?.values?.() || []);
  const panelData = realTimeArray.find(d => d.plant_id === plantId && d.panel_id === panelId);

  useEffect(() => {
    let cancelled = false;
    setHistoryStatus('loading');

    Promise.all(PANEL_HISTORY_FIELDS.map(field => fetchPanelFieldHistory(token, plantId, panelId, field, timeRange)))
      .then(results => {
        if (cancelled) return;
        const fieldResults = {};
        PANEL_HISTORY_FIELDS.forEach((field, i) => { fieldResults[field] = results[i]; });
        const merged = mergeFieldSeries(fieldResults).map(row => ({ ...row, time: format(new Date(row._time), 'HH:mm:ss') }));
        setHistoryData(merged);
        setHistoryStatus(merged.length > 0 ? 'ok' : 'empty');
      })
      .catch(() => {
        if (cancelled) return;
        setHistoryData([]);
        setHistoryStatus('error');
      });

    return () => { cancelled = true; };
  }, [token, plantId, panelId, timeRange]);

  const renderChartState = () => {
    if (historyStatus === 'loading') return <LoadingState label="Chargement de l'historique..." />;
    if (historyStatus === 'error') return <ErrorState label="Erreur lors du chargement de l'historique" />;
    return <EmptyState label="Aucune donnée historique pour cette période" />;
  };

  const breadcrumbItems = [
    { label: 'Tableau de bord', to: '/' },
    { label: 'Centrales', to: '/plants' },
    { label: plantMeta?.name || plantId, to: `/plants/${plantId}` },
    { label: `Panneau ${panelId}` }
  ];

  if (!panelData) {
    return (
      <div className="page">
        <Breadcrumbs items={breadcrumbItems} />
        <div className="page-header"><h1><Sun size={22} className="icon-sun" /> Panneau {panelId}</h1></div>
        <EmptyState label="Aucune donnée disponible pour ce panneau" />
      </div>
    );
  }

  // A photovoltaic "efficiency" KPI previously lived here
  // (power_w / (light_lvl * 10)) - removed (not renamed, not replaced): the
  // weather station's light_lvl is a unitless 0-100 normalized reading, not
  // measured irradiance in W/m2, and there is no panel area/installed
  // capacity anywhere in this system's telemetry. There is no physically
  // valid way to compute panel efficiency from what is actually measured.
  // nominalEfficiencyPct below is a manufacturer datasheet spec, shown
  // clearly labeled as such - it must never be confused with (or feed) a
  // live-measured efficiency figure, which this project still cannot compute.

  const specRows = [
    ['Fabricant', plantMeta?.panel_manufacturer],
    ['Modèle', plantMeta?.panel_model],
    ['Puissance nominale', plantMeta?.panel_rated_power_wp != null ? `${plantMeta.panel_rated_power_wp} Wp` : null],
    ['Tension à vide — Voc', plantMeta?.voc_v != null ? `${plantMeta.voc_v} V` : null],
    ['Tension au PPM — Vmp', plantMeta?.vmp_v != null ? `${plantMeta.vmp_v} V` : null],
    ['Courant de court-circuit — Isc', plantMeta?.isc_a != null ? `${plantMeta.isc_a} A` : null],
    ['Courant au PPM — Imp', plantMeta?.imp_a != null ? `${plantMeta.imp_a} A` : null],
    ['Rendement nominal constructeur', plantMeta?.nominal_efficiency_pct != null ? `${plantMeta.nominal_efficiency_pct} %` : null]
  ].filter(([, value]) => value !== null && value !== undefined);

  return (
    <div className="page">
      <Breadcrumbs items={breadcrumbItems} />
      <div className="page-header">
        <h1><Sun size={22} className="icon-sun" /> Panneau {panelId}</h1>
        <div className="time-range-selector">
          <button className={timeRange === '1h' ? 'active' : ''} onClick={() => setTimeRange('1h')}>1H</button>
          <button className={timeRange === '6h' ? 'active' : ''} onClick={() => setTimeRange('6h')}>6H</button>
          <button className={timeRange === '24h' ? 'active' : ''} onClick={() => setTimeRange('24h')}>24H</button>
          <button className={timeRange === '7d' ? 'active' : ''} onClick={() => setTimeRange('7d')}>7J</button>
        </div>
      </div>

      <div className="kpi-grid">
        <div className="kpi-card kpi-primary">
          <div className="kpi-icon"><Zap /></div>
          <div className="kpi-content"><div className="kpi-label">Puissance</div><div className="kpi-value">{(panelData.power_w || 0).toFixed(2)}</div><div className="kpi-unit">W</div></div>
        </div>
        <div className="kpi-card kpi-success">
          <div className="kpi-icon"><Battery /></div>
          <div className="kpi-content"><div className="kpi-label">Tension</div><div className="kpi-value">{(panelData.voltage_v || 0).toFixed(2)}</div><div className="kpi-unit">V</div></div>
        </div>
        <div className="kpi-card kpi-warning">
          <div className="kpi-icon"><Thermometer /></div>
          <div className="kpi-content">
            <div className="kpi-label">Température</div>
            {typeof panelData.temp_panel_c === 'number' ? (
              <><div className="kpi-value">{panelData.temp_panel_c.toFixed(1)}</div><div className="kpi-unit">°C</div></>
            ) : (
              <div className="kpi-value" style={{ fontSize: '1rem' }}>Capteur indisponible</div>
            )}
          </div>
        </div>
        <div className="kpi-card kpi-secondary">
          <div className="kpi-icon"><Activity /></div>
          <div className="kpi-content"><div className="kpi-label">Courant</div><div className="kpi-value">{(panelData.current_a || 0).toFixed(2)}</div><div className="kpi-unit">A</div></div>
        </div>
        <div className="kpi-card">
          <div className="kpi-content"><div className="kpi-label">Dernière mise à jour</div><div className="kpi-value" style={{ fontSize: '1.2rem' }}>{panelData.lastUpdate ? format(new Date(panelData.lastUpdate), 'HH:mm:ss') : format(new Date(), 'HH:mm:ss')}</div></div>
        </div>
      </div>

      <div className="charts-container">
        <div className="chart-panel chart-main">
          <div className="chart-header"><h3>Puissance (historique réel)</h3></div>
          {historyStatus !== 'ok' ? renderChartState() : (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={historyData}>
                <defs><linearGradient id="colorPanelPower" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} /><stop offset="95%" stopColor="#8884d8" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                <XAxis dataKey="time" stroke="#888" tick={{ fill: '#888' }} />
                <YAxis stroke="#888" tick={{ fill: '#888' }} />
                <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #444', borderRadius: '8px' }} />
                <Legend />
                <Area type="monotone" dataKey="power_w" stroke="#8884d8" fillOpacity={1} fill="url(#colorPanelPower)" name="Puissance (W)" connectNulls={false} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="chart-panel">
          <div className="chart-header"><h3>Tension & Courant (historique réel)</h3></div>
          {historyStatus !== 'ok' ? renderChartState() : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={historyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                <XAxis dataKey="time" stroke="#888" tick={{ fill: '#888' }} />
                <YAxis stroke="#888" tick={{ fill: '#888' }} />
                <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #444', borderRadius: '8px' }} />
                <Legend />
                <Line type="monotone" dataKey="voltage_v" stroke="#82ca9d" name="Tension (V)" connectNulls={false} />
                <Line type="monotone" dataKey="current_a" stroke="#ffc658" name="Courant (A)" connectNulls={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="chart-panel">
          <div className="chart-header"><h3>Température (historique réel)</h3></div>
          {historyStatus !== 'ok' ? renderChartState() : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={historyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                <XAxis dataKey="time" stroke="#888" tick={{ fill: '#888' }} />
                <YAxis stroke="#888" tick={{ fill: '#888' }} />
                <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #444', borderRadius: '8px' }} />
                <Legend />
                <Line type="monotone" dataKey="temp_panel_c" stroke="#ff8042" name="Température (°C)" connectNulls={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="kpi-grid" style={{ marginTop: 20 }}>
        <div className="kpi-card"><div className="kpi-content"><div className="kpi-label">ID Centrale</div><div className="kpi-value" style={{ fontSize: '1.5rem' }}>{panelData.plant_id}</div></div></div>
        <div className="kpi-card"><div className="kpi-content"><div className="kpi-label">ESP ID</div><div className="kpi-value" style={{ fontSize: '1.5rem' }}>{panelData.esp_id || 'N/A'}</div></div></div>
      </div>

      {specRows.length > 0 && (
        <div className="section" style={{ marginTop: 20 }}>
          <h4 className="section-title" style={{ fontSize: '0.95rem' }}>Spécifications du Panneau Photovoltaïque</h4>
          <div className="summary-sub" style={{ marginBottom: 10 }}>
            Spécifications déclarées par l'administrateur (fiche fabricant) - pas des valeurs mesurées en temps réel.
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <tbody>
                {specRows.map(([label, value]) => (
                  <tr key={label}><td>{label}</td><td>{value}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
